import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react'
import { Camera, FileUp, RefreshCw, Square, Video } from 'lucide-react'
import { newId, type Coordinates } from '@cht/shared'
import { cleanMediaBlob, selectRecordingType } from '../core/capture/media.js'
import { MediaStore, type StoredMedia } from '../core/capture/media-store.js'
import { WebCamera } from '../core/platform/camera.js'
import { Button, ConfirmSheet, InlineNotice } from './components.js'

type Screen = 'launcher' | 'primer' | 'camera' | 'review' | 'queue'
type Capture = { blob: Blob; url: string; kind: 'photo' | 'video'; mimeType: string; capturedAt: number }
const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'])
const camera = new WebCamera()

export function MediaCapture({ store, items, online, fix, fence, captureBlocked = false, onPlacePin, onItemsChanged }: {
  store: MediaStore
  items: StoredMedia[]
  online: boolean
  fix?: Coordinates
  fence: 'checking' | 'inside' | 'outside'
  captureBlocked?: boolean
  onPlacePin?: () => void
  onItemsChanged: () => Promise<void>
}) {
  const [screen, setScreen] = useState<Screen>('launcher')
  const [mode, setMode] = useState<'photo' | 'video'>('photo')
  const [stream, setStream] = useState<MediaStream>()
  const [capture, setCapture] = useState<Capture>()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [opening, setOpening] = useState(false)
  const [recording, setRecording] = useState(false)
  const [saving, setSaving] = useState(false)
  const [playbackFailed, setPlaybackFailed] = useState(false)
  const [recordedSeconds, setRecordedSeconds] = useState(0)
  const [error, setError] = useState<string>()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const recorderRef = useRef<MediaRecorder | undefined>(undefined)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream
      void videoRef.current.play().catch(() => undefined)
    }
  }, [stream, screen])
  useEffect(() => {
    if (!recording) return
    const timer = setInterval(() => setRecordedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000)), 250)
    return () => clearInterval(timer)
  }, [recording])
  useEffect(() => () => {
    if (stream) camera.stop(stream)
    if (capture) URL.revokeObjectURL(capture.url)
  }, [stream, capture])

  function chooseMode(next: 'photo' | 'video') {
    setMode(next); setError(undefined); setScreen('primer')
  }

  async function openCamera() {
    setOpening(true); setError(undefined)
    try {
      const opened = await camera.open({ video: { facingMode: { ideal: 'environment' } }, audio: mode === 'video' })
      setStream(opened); setScreen('camera')
    } catch {
      setError('Camera access is blocked. Allow camera access for this site, or choose a file instead.')
    } finally { setOpening(false) }
  }

  function closeCamera() {
    if (stream) camera.stop(stream)
    setStream(undefined); setRecording(false); setConfirmCancel(false); recorderRef.current = undefined
    setScreen('launcher')
  }

  async function takePhoto() {
    const video = videoRef.current
    if (!video || !video.videoWidth || !video.videoHeight) { setError('The camera is still starting. Try again in a moment.'); return }
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth; canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) { setError('The photo could not be created. Try again.'); return }
    review(cleanMediaBlob(blob, 'image/jpeg'), 'photo')
  }

  function startVideo() {
    if (!stream) return
    try {
      const mimeType = selectRecordingType((type) => MediaRecorder.isTypeSupported(type))
      const recorder = new MediaRecorder(stream, { mimeType })
      chunksRef.current = []
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data) }
      recorder.onstop = () => {
        try { review(cleanMediaBlob(new Blob(chunksRef.current, { type: mimeType }), mimeType), 'video') }
        catch (reason) { setError(reason instanceof Error ? reason.message : 'That recording is empty. Record it again.') }
      }
      recorder.start(1_000); recorderRef.current = recorder; startedAtRef.current = Date.now(); setRecordedSeconds(0); setRecording(true)
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Video recording is not supported on this device.') }
  }

  function stopVideo() {
    const recorder = recorderRef.current
    if (recorder?.state === 'recording' || recorder?.state === 'paused') recorder.stop()
    setRecording(false)
  }

  function askToCancel() {
    const recorder = recorderRef.current
    if (recording && recorder?.state === 'recording') recorder.pause()
    setConfirmCancel(true)
  }

  function keepRecording() {
    const recorder = recorderRef.current
    if (recording && recorder?.state === 'paused') {
      const pausedFor = Date.now() - (startedAtRef.current + recordedSeconds * 1_000)
      startedAtRef.current += Math.max(0, pausedFor)
      recorder.resume()
    }
    setConfirmCancel(false)
  }

  function discardRecording() {
    const recorder = recorderRef.current
    if (recorder) recorder.onstop = null
    if (recorder?.state === 'recording' || recorder?.state === 'paused') recorder.stop()
    closeCamera()
  }

  function switchCameraMode() {
    if (stream) camera.stop(stream)
    setStream(undefined); setMode(mode === 'photo' ? 'video' : 'photo'); setError(undefined); setScreen('primer')
  }

  function review(blob: Blob, kind: 'photo' | 'video') {
    if (stream) camera.stop(stream)
    setStream(undefined); setRecording(false)
    const cleaned = cleanMediaBlob(blob, kind === 'photo' ? 'image/jpeg' : 'video/webm')
    setCapture({ blob: cleaned, url: URL.createObjectURL(cleaned), kind, mimeType: cleaned.type, capturedAt: Date.now() })
    setTitle(''); setDescription(''); setPlaybackFailed(false); setScreen('review')
  }

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const mimeType = file.type.split(';')[0]!.toLowerCase()
    if (!allowedTypes.has(mimeType)) { setError('Choose a JPEG, PNG, WebP, MP4, WebM, or QuickTime file.'); return }
    try { review(cleanMediaBlob(file, mimeType), mimeType.startsWith('image/') ? 'photo' : 'video') }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'That file could not be used.') }
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (!capture) return
    setError(undefined); setSaving(true)
    try {
      await store.create({
        id: newId(), kind: capture.kind, mimeType: capture.mimeType, blob: capture.blob,
        title: title.trim(), description: description.trim(), capturedAt: capture.capturedAt,
        ...(fence === 'inside' && fix ? { coordinates: fix } : {})
      }, online)
      URL.revokeObjectURL(capture.url); setCapture(undefined); await onItemsChanged(); setScreen('queue')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'This device could not save the capture. Free storage and try again.')
    } finally { setSaving(false) }
  }

  async function retry(item: StoredMedia) { await store.retry(item.id); await onItemsChanged() }

  if (screen === 'primer') return <div className="capture-panel">
    <InlineNotice tone="info" title={`${mode === 'photo' ? 'Camera' : 'Camera and microphone'} permission`}>Your browser will ask once. Capture happens inside this app and returns to a review screen before anything is saved.</InlineNotice>
    {error && <InlineNotice tone="fail" title="Camera unavailable" action={<Button type="button" variant="secondary" icon={FileUp} onClick={() => fileRef.current?.click()}>Choose a file</Button>}>{error}</InlineNotice>}
    <input ref={fileRef} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" onChange={chooseFile} />
    <div className="button-row"><Button type="button" variant="secondary" onClick={() => setScreen('launcher')}>Cancel</Button><Button type="button" loading={opening} onClick={() => void openCamera()}>Continue</Button></div>
  </div>

  if (screen === 'camera') return <div className="capture-panel capture-panel--camera">
    <video ref={videoRef} className="camera-preview" muted playsInline aria-label="Live camera preview" />
    {fence !== 'inside' && <InlineNotice tone="pending" title="Location will not be attached">You can capture here, but coordinates are kept only while GPS confirms you are on the property.</InlineNotice>}
    {error && <InlineNotice tone="fail" title="Capture unavailable">{error}</InlineNotice>}
    {recording && <p className="recording-time" aria-live="polite">RECORDING · {formatDuration(recordedSeconds)}</p>}
    {confirmCancel ? <ConfirmSheet title={`Discard ${formatDuration(recordedSeconds)}?`} onCancel={keepRecording} actions={<><Button type="button" variant="secondary" onClick={keepRecording}>Resume recording</Button><Button type="button" variant="danger" onClick={discardRecording}>Discard</Button></>}>This recording has not been saved.</ConfirmSheet> : <div className="capture-actions">
      <Button type="button" variant="secondary" onClick={recording ? askToCancel : closeCamera}>Cancel</Button>
      {mode === 'photo'
        ? <button type="button" className="shutter" aria-label="Take photo" onClick={() => void takePhoto()}><Camera aria-hidden="true" /></button>
        : recording
          ? <button type="button" className="shutter shutter--stop" aria-label="Stop recording" onClick={stopVideo}><Square aria-hidden="true" fill="currentColor" /></button>
          : <button type="button" className="shutter" aria-label="Start recording" onClick={startVideo}><Video aria-hidden="true" /></button>}
      {!recording && <Button type="button" variant="secondary" onClick={switchCameraMode}>{mode === 'photo' ? 'Video' : 'Photo'}</Button>}
    </div>}
  </div>

  if (screen === 'review' && capture) return <form className="capture-panel media-review" onSubmit={(event) => void save(event)}>
    {capture.kind === 'photo' ? <img src={capture.url} alt="New capture preview" /> : playbackFailed
      ? <InlineNotice tone="fail" title="Preview unavailable">The recording is still here. You can save it and try playback from the media library, or record again.</InlineNotice>
      : <video src={capture.url} controls playsInline aria-label="New video preview" onError={() => setPlaybackFailed(true)} />}
    <p className="data">{capture.kind.toUpperCase()} · CAPTURED {new Date(capture.capturedAt).toLocaleString()}</p>
    <p className="data">{fence === 'inside' && fix ? 'LOCATION ATTACHED' : fence === 'outside' ? 'LOCATION STRIPPED · OFF PROPERTY' : 'LOCATION UNAVAILABLE'}</p>
    <label htmlFor="media-title">Title (optional)</label><input id="media-title" maxLength={120} value={title} onChange={(event) => setTitle(event.target.value)} />
    <label htmlFor="media-description">Notes (optional)</label><textarea id="media-description" maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} />
    {!online && <InlineNotice tone="pending" title="Saved locally first">This capture will stay on this device and upload automatically when you reconnect.</InlineNotice>}
    {error && <InlineNotice tone="fail" title="Not saved">{error}</InlineNotice>}
    <div className="button-row"><Button type="button" variant="secondary" disabled={saving} onClick={() => { URL.revokeObjectURL(capture.url); setCapture(undefined); chooseMode(capture.kind) }}>Retake</Button><Button type="submit" loading={saving}>Use {capture.kind}</Button></div>
  </form>

  if (screen === 'queue') return <div className="capture-panel">
    <div className="capture-heading"><h2>Upload queue</h2><Button type="button" variant="secondary" onClick={() => setScreen('launcher')}>Done</Button></div>
    {items.length === 0 ? <p className="field-message">No captures on this device yet.</p> : <div className="upload-list">{items.map((item) => <div className="upload-row" key={item.id}>
      <span>{item.kind === 'photo' ? <Camera aria-hidden="true" /> : <Video aria-hidden="true" />}<span><strong>{item.title || `${item.kind === 'photo' ? 'Photo' : 'Video'} capture`}</strong><small>{syncLabel(item)}</small></span></span>
      {(item.syncState === 'retrying' || item.syncState === 'failed') && <Button type="button" size="sm" variant="secondary" icon={RefreshCw} disabled={!online} onClick={() => void retry(item)}>Retry</Button>}
    </div>)}</div>}
  </div>

  return <div className="capture-panel">
    <p>Capture a field record. Photos and videos are saved on this device before upload.</p>
    {captureBlocked && <InlineNotice tone="fail" title="Storage full" action={onPlacePin ? <Button type="button" variant="secondary" onClick={onPlacePin}>Place a pin instead</Button> : undefined}>Photo, video, and file import are blocked until media is deleted. Pins remain available.</InlineNotice>}
    {fence === 'outside' && <InlineNotice tone="pending" title="Off property">Capture remains available, but the app will strip coordinates.</InlineNotice>}
    {error && <InlineNotice tone="fail" title="File unavailable">{error}</InlineNotice>}
    <div className="capture-launcher">
      <Button type="button" size="lg" icon={Camera} disabled={captureBlocked} onClick={() => chooseMode('photo')}>Photo</Button>
      <Button type="button" size="lg" icon={Video} disabled={captureBlocked} onClick={() => chooseMode('video')}>Video</Button>
      <Button type="button" variant="secondary" icon={FileUp} disabled={captureBlocked} onClick={() => fileRef.current?.click()}>Choose a file</Button>
    </div>
    <input ref={fileRef} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" onChange={chooseFile} />
    <Button type="button" variant="ghost" onClick={() => setScreen('queue')}>View upload queue ({items.filter((item) => item.syncState !== 'synced').length})</Button>
  </div>
}

function formatDuration(seconds: number): string {
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`
}

function syncLabel(item: StoredMedia): string {
  if (item.syncState === 'synced') return 'SYNCED'
  if (item.syncState === 'uploading') return `UPLOADING ${item.progressPercent ?? 0}%`
  if (item.syncState === 'queued') return 'QUEUED'
  if (item.syncState === 'retrying') return `RETRYING · ${item.lastError ?? 'waiting for connection'}`
  return `NEEDS ATTENTION · ${item.lastError ?? 'upload rejected'}`
}
